import { useEffect, useRef, useState } from 'react'
import { ProgressBar } from './progress-bar'

/**
 * The numbered steps with the loader bar under them, shared by deposit,
 * withdraw and swap progress. `step` is the current index, -1 for a
 * failure; `done` fills the bar.
 *
 * `idle` is a flow that has not started (a deposit address nobody has sent
 * to yet): no step is current and the bar does not move.
 *
 * The bar never sits still: within a step it eases toward the next step's
 * start (fast at first, then inching) so a flow feels like it is moving
 * the whole time, and it only jumps when the real step advances. It never
 * moves backwards and never reaches the next step on its own.
 */
/** where each flow's bar got to, by its record id: a dialog closed and reopened picks up there instead of easing from the step's start again (John, Nov 1 2026) */
const remembered = new Map<string, number>()

export function Steps({ labels, step, done = false, idle = false, flowKey }: { labels: readonly string[]; step: number; done?: boolean; idle?: boolean; flowKey?: string }) {
  const failed = step < 0
  const count = labels.length
  const [value, setValue] = useState(() => (done ? 100 : Math.max(flowKey !== undefined ? (remembered.get(flowKey) ?? 0) : 0, Math.max(0, step) * (100 / count))))
  const latest = useRef(value)
  latest.current = value
  // the record may arrive a render after the card (the dialog opens on its placeholder first): pick up then too
  useEffect(() => {
    if (flowKey === undefined) return
    const seen = remembered.get(flowKey)
    if (seen !== undefined) setValue((v) => Math.max(v, seen))
  }, [flowKey])
  useEffect(() => {
    if (flowKey !== undefined) remembered.set(flowKey, value)
  }, [flowKey, value])
  useEffect(() => {
    if (done) {
      setValue(100)
      return
    }
    if (failed || idle) {
      // idle: nothing has started yet, so the bar waits where it is
      return
    }
    const segment = 100 / count
    const start = step * segment
    const ceiling = (step + 1) * segment - segment * 0.08
    setValue((v) => Math.max(v, start))
    const id = setInterval(() => {
      const v = latest.current
      if (v >= ceiling) return
      // ease toward the ceiling, but always at least a hair per tick
      const eased = (ceiling - v) * 0.03
      const next = Math.min(ceiling, v + Math.max(eased, segment * 0.004))
      setValue(next)
    }, 250)
    return () => clearInterval(id)
  }, [step, done, failed, idle, count])
  return (
    <div className="grid gap-3">
      <ol className="flex items-center justify-between gap-2 text-xs">
        {labels.map((label, i) => {
          const state = failed ? 'error' : done || i < step ? 'done' : i === step && !idle ? 'current' : 'todo'
          return (
            <li key={label} className="flex flex-1 flex-col items-center gap-1">
              <span
                className={`flex size-6 items-center justify-center rounded-full border text-[11px] font-semibold ${
                  state === 'done' || state === 'current' ? 'border-accent text-accent' : state === 'error' ? 'border-negative text-negative' : 'border-line text-muted'
                }`}
              >
                {i + 1}
              </span>
              <span className={state === 'todo' ? 'text-muted' : ''}>{label}</span>
            </li>
          )
        })}
      </ol>
      <ProgressBar value={failed ? latest.current : value} />
    </div>
  )
}
